"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import type { ForumTopicDetail } from "@/types/forum";
import { TOKEN_KEY } from "@/constants/auth";
import { forumApi, toast } from "@/lib/forum.client";
import { catPath, fa, looksLikeHtml, tagPath, timeAgo } from "@/lib/forum-format";
import { useForumAuth } from "./useForumAuth";
import ReportBox from "./ReportBox";
import { avatar, btn, card, catBadge, hint, input, qGrid, solvedBadge, tagChip, voteBtn } from "./ui";

const Up = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
        <polyline points="18 15 12 9 6 15" />
    </svg>
);

/** کارت پرسش + پاسخ‌ها + فرم پاسخ. با داده‌ی SSR رندر می‌شود و برای کاربر لاگین‌شده وضعیت رأی‌ها را تازه می‌کند. */
export default function TopicThread({ initial }: { initial: ForumTopicDetail }) {
    const { initial: myInitial, guard, handleError } = useForumAuth();
    const [t, setT] = useState<ForumTopicDetail>(initial);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try {
            setT(await forumApi.topic(initial.id));
        } catch {
            /* نمایش داده‌ی قبلی */
        }
    }, [initial.id]);

    useEffect(() => {
        forumApi.hit(initial.id);
        if (localStorage.getItem(TOKEN_KEY)) refresh();
    }, [initial.id, refresh]);

    const voteTopic = async () => {
        if (!guard()) return;
        try {
            const r = await forumApi.voteTopic(t.id);
            setT((p) => ({ ...p, vote: r.votes, voted: r.voted }));
        } catch (e) {
            handleError(e);
        }
    };

    const voteAnswer = async (id: number) => {
        if (!guard()) return;
        try {
            const r = await forumApi.voteAnswer(id);
            setT((p) => ({
                ...p,
                answers: p.answers.map((a) => (a.id === id ? { ...a, votes: r.votes, voted: r.voted } : a)),
            }));
        } catch (e) {
            handleError(e);
        }
    };

    const markBest = async (id: number) => {
        if (!guard()) return;
        try {
            await forumApi.markBest(id);
            toast("وضعیت پاسخ درست به‌روز شد.");
            await refresh();
        } catch (e) {
            handleError(e);
        }
    };

    const send = async () => {
        if (!guard()) return;
        if (reply.trim().length < 5) return toast("متن پاسخ را بنویسید.");
        setBusy(true);
        try {
            await forumApi.createAnswer(t.id, reply.trim());
            setReply("");
            toast("پاسخ شما ثبت شد.");
            await refresh();
        } catch (e) {
            handleError(e);
        } finally {
            setBusy(false);
        }
    };

    const html = looksLikeHtml(t.body);

    return (
        <article className={`${card} ${qGrid} px-[18px] pb-3.5 pt-[18px]`}>
            <div className="flex flex-col items-center gap-0.5">
                <button type="button" className={voteBtn(t.voted)} onClick={voteTopic} aria-label="رأی مثبت به پرسش" aria-pressed={t.voted}>
                    <Up />
                </button>
                <b className="text-[15px] leading-normal">{fa(t.vote)}</b>
                <span className="text-[11px] text-gray-500">رأی</span>
            </div>

            <div className="min-w-0">
                <h1 className="text-[18px] font-extrabold leading-[1.9]">
                    {t.title}
                    {t.solved && <span className={solvedBadge}>حل‌شده</span>}
                </h1>

                {html ? (
                    <div className="my-1.5 mb-3.5 text-[14.5px] leading-loose text-gray-700 [&_img]:h-auto [&_img]:max-w-full" dangerouslySetInnerHTML={{ __html: t.body }} />
                ) : (
                    <p className="my-1.5 mb-3.5 whitespace-pre-line text-[14.5px] leading-loose text-gray-700">{t.body}</p>
                )}

                <div className="flex flex-wrap items-center gap-2.5 text-[12.5px] text-gray-500 thread">
                    {t.category && (
                        <Link href={catPath(t.category.slug)} className={catBadge(t.category.color)}>
                            {t.category.name}
                        </Link>
                    )}
                    {t.tags.map((g) => (
                        <Link key={g.slug} href={tagPath(g.slug)} className={tagChip} rel="tag">{g.name}</Link>
                    ))}
                    <span className="mr-auto flex items-center gap-2 max-md:mr-0">
                        <div className={avatar("sm")}>{t.author_name.trim().charAt(0)}</div>
                        <span><b className="font-bold text-gray-900">{t.author_name}</b> · <time dateTime={t.created_at}>{timeAgo(t.created_at)}</time> · {fa(t.view)} بازدید</span>
                    </span>
                    <ReportBox target={{ topic_id: t.id }} label="گزارش پرسش" />
                </div>
            </div>

            <div className="col-span-full mt-3 border-t border-gray-200 pt-3.5" id="answers">
                <h2 className="mb-1 text-[15px] font-extrabold">{fa(t.answers.length)} پاسخ</h2>
                {t.answers.length === 0 && <div className="py-1.5 text-[13.5px] text-gray-500">هنوز پاسخی ثبت نشده. اولین پاسخ را شما بدهید.</div>}

                {t.answers.map((a) => (
                    <div className={`${qGrid} border-b border-dashed border-gray-200 py-3`} key={a.id} id={`answer-${a.id}`}>
                        <div className="flex flex-col items-center gap-0.5 pt-1.5">
                            <button type="button" className={voteBtn(a.voted)} onClick={() => voteAnswer(a.id)} aria-label="رأی مثبت به پاسخ" aria-pressed={a.voted}>
                                <Up />
                            </button>
                            <b className="text-[15px] leading-normal">{fa(a.votes)}</b>
                        </div>
                        <div className={`rounded-xl px-4 py-3 ${a.is_best ? "border border-emerald-200 bg-emerald-50 dark:bg-emerald-50/20 dark:border-emerald-50/30" : "bg-gray-100"}`}>
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                <div className={avatar("sm")}>{a.author_name.trim().charAt(0)}</div>
                                <b className="text-[13.5px]">{a.author_name}</b>
                                {a.expert && <span className="rounded-full bg-gray-900 px-[9px] text-[11.5px] font-bold text-white">کارشناس</span>}
                                {a.is_best && <span className="rounded-full bg-emerald-700 px-[9px] text-[11.5px] font-bold text-white">پاسخ تأییدشده</span>}
                                <span className={`${hint} mr-auto`}>{timeAgo(a.created_at)}</span>
                            </div>
                            <p className="whitespace-pre-line text-sm text-gray-700">{a.body}</p>
                            <div className="mt-1.5 flex items-center gap-3.5 text-[12.5px] text-gray-500">
                                {t.can_manage && (
                                    <button type="button" className="cursor-pointer text-[12.5px] font-bold text-[#4451A3] dark:text-white" onClick={() => markBest(a.id)}>
                                        {a.is_best ? "لغو تأیید پاسخ" : "تأیید به‌عنوان پاسخ درست"}
                                    </button>
                                )}
                                <ReportBox target={{ answer_id: a.id }} />
                            </div>
                        </div>
                    </div>
                ))}

                <div className="mt-3.5 flex items-start gap-2.5">
                    <div className={avatar("sm", "a")}>{myInitial}</div>
                    <textarea
                        className={`${input} min-h-[62px] flex-1 resize-y text-sm`}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        maxLength={5000}
                        aria-label="پاسخ شما"
                        placeholder="پاسخ یا تجربه‌ی خود را بنویسید"
                    />
                    <button className={btn("primary", true)} type="button" onClick={send} disabled={busy}>
                        {busy ? "…" : "ارسال پاسخ"}
                    </button>
                </div>
            </div>
        </article>
    );
}
